import React, {useRef} from 'react';
import NobulaLogoIcon from '../../../../../../assets/icons/NobulaLogo.svg';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {ImageFitEnum} from '../../../../../incase-domain/common/imageFit';
import Button from '../../../../../incase-domain/components/button/Button';
import {getAnswerColors} from '../common/utils';
import AnswerComment from '../question/AnswerComment';
import {AnswerTypes} from '../question/QuestionAnswer';
import {useSlidePreviewCapture} from '../../../../../context/SlidePreviewCaptureContext';

interface IPhotoQuestionAnswerProps {
    answer: string;
    imgSrc: string;
    answerType: AnswerTypes;
    imageFit?: ImageFitEnum;
    comment?: string;
    onClick: () => void;
    clicked: boolean;
    correctColor?: string;
    incorrectColor?: string;
    debatableColor?: string;
    answered?: boolean;
    setImgModalState: () => void;
    showAllComments?: boolean;
    device?: DeviceType;
    showComment?: boolean;
    setShowComment?: (show: boolean) => void;
}

function PhotoQuestionAnswer({
    answer,
    answerType,
    comment,
    onClick,
    correctColor,
    incorrectColor,
    debatableColor,
    imgSrc,
    imageFit,
    clicked,
    answered,
    setImgModalState,
    showAllComments,
    device = DeviceType.DESKTOP,
    showComment = false,
    setShowComment,
}: IPhotoQuestionAnswerProps): JSX.Element {
    const photoQuestionAnswerRef = useRef<HTMLDivElement>(null);
    const isSlidePreviewCapture = useSlidePreviewCapture();

    const {getBtnAnswerColor, getCustomAnswerColor, getCustomAnswerColorWithAlpha} = getAnswerColors(
        answerType,
        correctColor,
        incorrectColor,
        debatableColor
    );
    const answerSelectedOrAnswered = clicked || answered;
    const shouldRenderComment = !!comment && (isSlidePreviewCapture || (answered && (showAllComments || clicked)));

    let selectionIcon = 'solar:stop-linear';
    if (clicked) {
        selectionIcon = 'solar:check-square-bold';
    } else if (answered && !clicked) {
        selectionIcon = 'solar:stop-bold';
    }

    return (
        <div ref={photoQuestionAnswerRef} className="c-photoAnswerQuestion__answer">
            <div
                className={`c-photoAnswerQuestion__answer_data ${clicked ? 'c-photoAnswerQuestion__answer_data--selected' : ''} ${
                    answered ? 'c-photoAnswerQuestion__answer_data--answered' : ''
                }`}
                onClick={onClick}
            >
                <img src={imgSrc || NobulaLogoIcon} style={{objectFit: imageFit}} alt={answer} />

                {answerSelectedOrAnswered && <div className="c-photoAnswerQuestion__answer_overlay" />}

                <div className="c-photoAnswerQuestion__answer_selection">
                    <Button
                        variant="ghost"
                        size="2xl"
                        padded={false}
                        color={answered || !clicked ? getBtnAnswerColor() : 'info'}
                        customColor={getCustomAnswerColorWithAlpha(0.35)}
                        customIconColor={getCustomAnswerColor()}
                        dynamicSize
                        leading={selectionIcon}
                    />
                </div>

                <div className="c-photoAnswerQuestion__answer_zoom">
                    <Button
                        leading="solar:magnifer-linear"
                        variant="ghost"
                        color="primary"
                        onClick={(e) => {
                            e?.stopPropagation();
                            setImgModalState();
                        }}
                        dynamicSize
                        disableHover
                        padded={false}
                        size="2xl"
                    />
                </div>
            </div>

            {shouldRenderComment && (
                <AnswerComment
                    answer={answer}
                    comment={comment as string}
                    showComment={showComment}
                    setShowComment={setShowComment}
                    anchorRef={photoQuestionAnswerRef}
                    device={device}
                />
            )}
        </div>
    );
}

export default PhotoQuestionAnswer;
